import { withManifest } from '@godaddy/gasket-hcs';
import * as React from 'react';
import PropTypes from 'prop-types';
import presets from './presets.json';
import UtilityBarClientWrapper from './utility-bar-client-wrapper';
import { HivemindContext } from '@ux/hivemind-provider';

// css for a11y skip to main component
import '@godaddy/gasket-hcs/dist/styles.css';

import '@ux/account-delegation/styles';
import '@ux/browser-deprecation-banner/styles';
import './header.css';

// adapted from legacy sales-header and utility-header transform.js files
// ticket to extract this function to header-utils: https://godaddy-corp.atlassian.net/browse/UXP-8204
/**
   * Orders market data from all function retrieval
   *
   * @param {Object} marketData Market data
   * @returns {Object} Ordered market data
   * @private
   */
function orderMarketData(marketData) {
  const orderedMarketData = Object.keys(marketData) // ar-ae
    .filter(market => !marketData[market].internal)
    .sort((a, b) => {
      const lmarket = marketData[a];
      const rmarket = marketData[b];

      //
      // Use the country code to compare the countryName
      // because those are not guaranteed to be equal.
      // (e.g. België - Nederlands and Belgique - Français)
      //
      if (lmarket.countryCode === rmarket.countryCode) {
        //
        // If the languageName is also identical, it is the same.
        // If they are both not the primary language, sort by languageName
        // ensuring locale awareness.
        //
        if (lmarket.languageName === rmarket.languageName) { return 0; }
        if (!lmarket.primaryLanguageForCountry && !rmarket.primaryLanguageForCountry) {
          return (lmarket.languageName || '').localeCompare(rmarket.languageName);
        }

        //
        // Ensure the primary language is always higher in the list
        //
        if (lmarket.primaryLanguageForCountry) { return -1; }
        if (rmarket.primaryLanguageForCountry) { return 1; }
      }

      //
      // If they are different countries, sort by countryName
      // ensuring locale awareness.
      //
      return (lmarket.countryName || '').localeCompare(rmarket.countryName);
    }).reduce((acc, market) => {
      acc[market] = marketData[market];
      return acc;
    }, {});
  return orderedMarketData;
}

/**
 * React Component to render the Header.
 * This component must be present, but may return null if no header is required.
 *
 * @param {Object} props The props for this component
 * @returns {React.ReactElement} JSX for header
 */
export function Header({ marketData = {}, ...props }) {
  const { privateLabelId, requestedHeader } = props;
  const isReseller = privateLabelId > 1;
  let headerPreset = requestedHeader;
  if (requestedHeader === 'language-header' && isReseller) {
    headerPreset = 'language-header-pl';
  }

  const { components: originalComponents } = presets[headerPreset] || {};
  const components = JSON.parse(JSON.stringify(originalComponents));

  const orderedMarketData = React.useMemo(() => {
    return orderMarketData(marketData);
  }, [marketData]);

  if (!components) {
    throw new Error(
      'Header preset not defined. This service serves the following headers: ' +
      Object.keys(presets).join(', ')
    );
  }

  const { ready, client } = React.useContext(HivemindContext);
  if (ready) {
    const experimentId = '123_hide_contact_number_AB';
    // NOTE: we can use an ID here but we need a label for the PCS

    const experimentCohorts = client.getCohorts({
      experiments: [experimentId],
      attributes: {
        app: props.app,
        privateLabelId,
        requestedHeader
      }
    });


    if (experimentCohorts?.cohorts?.[experimentId]?.cohortId === 'b_variant') {
      components.contactTray = false;
    }
  }

  return <UtilityBarClientWrapper
    { ...props }
    components={ components }
    preset={ requestedHeader }
    manifest={ requestedHeader }
    marketData={ orderedMarketData }
    marketSelector={{
      markets: Object.keys(orderedMarketData)
    }}
    isReseller={ isReseller }
  />;
}

/**
 * Export Header with Manifest HOC.
 *
 * @component
 * @public
 */
export default withManifest(Header, {
  renderAccountDelegation: true,
  initCustomerState: true,
  initTraffic: true
});

Header.propTypes = {
  privateLabelId: PropTypes.number.isRequired,
  requestedHeader: PropTypes.string.isRequired,
  marketData: PropTypes.object.isRequired,
  app: PropTypes.string.isRequired
};

